import { boothKeyOf } from '../../../../utils/boothMap';

/**
 * Pure view helpers of the hall-plan booking page (no React, no i18next).
 *
 * getEventBoothMap sends product-level data ONCE in `map.products` ({ [productId]: product }) and
 * slim `booths[]` (one entry per physical booth). hydrateBoothMap copies the product fields onto
 * every booth so the components keep reading booth.name / image / dimensionFile / model3d / area /
 * width / depth / height / type. Fields a booth already carries win (older Lambda versions sent them
 * per booth), so the page works with both map shapes.
 *
 * Offer model (docs/plan-standangebot-modell.md): booths may carry the plan features positionType,
 * zone, openSides, their offers[] ({ eventProductId, productId, via }), the price of THIS booth
 * (priceForBooth of the primary offer), priceDetails (the priceForBooth breakdown) and priceMode.
 * hydrateBooth sets every one of them (null / [] when the map does not send it), so older map
 * responses keep working.
 */

const PRODUCT_FIELDS = ['name', 'type', 'image', 'dimensionFile', 'model3d', 'width', 'depth', 'height'];

function present(value) {
  return value !== null && value !== undefined && value !== '';
}

/** priceDetails (AWSJSON: JSON string or object) as an object, null when missing or invalid. */
export function parsePriceDetails(value) {
  if (value === null || value === undefined || value === '') return null;
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
}

function finiteOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

const SURCHARGE_TYPES = ['percent', 'absolute', 'rate'];

/**
 * What the price breakdown of a booth has to explain, or null when there is nothing (fixed price
 * without surcharges and tier, as for LOUNGES 2028):
 * { surcharges: [{ on, key, type, value, amount }] (amount 0 left out), tier: { start, end, price } | null,
 *   minPriceApplied, mode, sqm, rate (€/m² of a perSqm price, else null), total }.
 */
export function priceBreakdownOf(priceDetailsValue) {
  const details = parsePriceDetails(priceDetailsValue);
  if (!details) return null;
  const surcharges = (Array.isArray(details.surcharges) ? details.surcharges : []).filter((s) => {
    if (!s || typeof s !== 'object' || !SURCHARGE_TYPES.includes(s.type)) return false;
    if (!present(s.key) || !String(s.key).trim()) return false;
    const amount = finiteOrNull(s.amount);
    return amount === null || amount !== 0;
  });
  const tier = details.tier && typeof details.tier === 'object' ? details.tier : null;
  const minPriceApplied = details.minPriceApplied === true;
  if (surcharges.length === 0 && !tier && !minPriceApplied) return null;
  const sqm = finiteOrNull(details.sqm);
  const base = finiteOrNull(details.base);
  const mode = details.mode === 'perSqm' ? 'perSqm' : 'fixed';
  const rate = mode === 'perSqm' && sqm && base !== null ? Math.round((base / sqm) * 100) / 100 : null;
  return { surcharges, tier, minPriceApplied, mode, sqm, rate, total: finiteOrNull(details.total) };
}

/** One booth of the map with its product fields filled in and a `boothKey`. */
export function hydrateBooth(booth, products) {
  if (!booth || typeof booth !== 'object') return booth;
  const product = products && booth.productId ? products[booth.productId] : null;
  const result = { ...booth, boothKey: booth.boothKey || boothKeyOf(booth) };
  result.positionType = present(booth.positionType) ? String(booth.positionType).trim() : null;
  result.zone = present(booth.zone) ? String(booth.zone).trim() : null;
  result.openSides = finiteOrNull(booth.openSides);
  result.offers = Array.isArray(booth.offers) ? booth.offers.filter((offer) => offer && typeof offer === 'object') : [];
  result.priceDetails = parsePriceDetails(booth.priceDetails);
  // the booth's own m² (BoothPlanArea.size, sent as variantSize) wins over the standard size of the offer
  if (!present(result.area) && present(booth.variantSize)) result.area = booth.variantSize;
  if (!product || typeof product !== 'object') {
    result.priceMode = present(booth.priceMode) ? booth.priceMode : null;
    return result;
  }
  for (const field of PRODUCT_FIELDS) {
    if (result[field] === undefined && product[field] !== undefined) result[field] = product[field];
  }
  if (!present(result.area)) {
    const productArea = present(product.area) ? product.area : product.boothArea;
    if (present(productArea)) result.area = productArea;
  }
  if (result.productVariant === undefined) result.productVariant = present(product.boothVariant) ? product.boothVariant : null;
  const productPriceMode = present(product.priceMode) ? product.priceMode : product.boothPriceMode;
  result.priceMode = present(booth.priceMode) ? booth.priceMode : present(productPriceMode) ? productPriceMode : null;
  return result;
}

/** The map with hydrated booths (other keys untouched). */
export function hydrateBoothMap(map) {
  if (!map || typeof map !== 'object' || !Array.isArray(map.booths)) return map;
  const products = map.products && typeof map.products === 'object' ? map.products : {};
  return { ...map, booths: map.booths.map((booth) => hydrateBooth(booth, products)) };
}

function featureValues(list) {
  const out = [];
  const seen = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    const raw = entry && typeof entry === 'object' ? entry.key : entry;
    if (!present(raw)) continue;
    const key = String(raw).trim();
    const id = key.toLowerCase();
    if (!key || seen.has(id)) continue;
    seen.add(id);
    out.push({ key, count: entry && typeof entry === 'object' ? finiteOrNull(entry.count) : null });
  }
  return out;
}

/**
 * Chip options of the filter bar ("offer first"): { positionTypes, zones, offerCodes }, each
 * [{ key, count }] and EMPTY unless the dimension has more than one value, so a plan without the
 * features (or with a single value) keeps today's bar.
 *  - positionTypes / zones from map.features (counts over the live areas);
 *  - offerCodes from map.variants (offer code, booth count) - only when at least one booth got its
 *    offer through a match rule (offers[0].via === 'rule'): plans that assign every booth by its plan
 *    code (LOUNGES 2028: 22 codes) keep the bar without offer chips.
 */
export function filterOptionsOf(map) {
  const empty = { positionTypes: [], zones: [], offerCodes: [] };
  if (!map || typeof map !== 'object') return empty;
  const features = map.features && typeof map.features === 'object' ? map.features : {};
  const positionTypes = featureValues(features.positionTypes);
  const zones = featureValues(features.zones);
  const byRule = (Array.isArray(map.booths) ? map.booths : []).some(
    (booth) => booth && Array.isArray(booth.offers) && booth.offers[0] && booth.offers[0].via === 'rule'
  );
  const offerCodes = byRule
    ? featureValues(
        (Array.isArray(map.variants) ? map.variants : []).map((v) => ({
          key: v ? v.variant : null,
          count: v ? (finiteOrNull(v.boothCount) !== null ? v.boothCount : v.total) : null,
        }))
      ).filter((entry) => entry.count === null || entry.count > 0)
    : [];
  return {
    positionTypes: positionTypes.length > 1 ? positionTypes : [],
    zones: zones.length > 1 ? zones : [],
    offerCodes: offerCodes.length > 1 ? offerCodes : [],
  };
}

/**
 * Exact span { min, max } of a numeric booth field (price, area) over the booths, ignoring
 * missing, invalid and negative values; { min: 0, max: 0 } when there is none. Not rounded: the
 * filter slider shows these real bounds (4.950–36.800 €, not 4.500–37.000 €) and only snaps its
 * thumbs to the step in between.
 */
export function valueSpan(booths, field) {
  let min = null;
  let max = null;
  for (const booth of Array.isArray(booths) ? booths : []) {
    const value = booth && typeof booth === 'object' ? finiteOrNull(booth[field]) : null;
    if (value === null || value < 0) continue;
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
  }
  return min === null ? { min: 0, max: 0 } : { min, max };
}

/** Booth with the given key (boothKeyOf), null when none. */
export function findBoothByKey(booths, key) {
  if (!key || !Array.isArray(booths)) return null;
  return booths.find((booth) => booth && boothKeyOf(booth) === key) || null;
}

/**
 * Variant data of a booth for the details panel: { variant, size, free, total } or null for a
 * single-booth product. `variants` = map.variants (one entry per variant EventProduct), `products` =
 * map.products (fallback for the variant code when the variants list is missing).
 */
export function variantInfoOf(booth, variants, products) {
  if (!booth) return null;
  const entry = (Array.isArray(variants) ? variants : []).find((v) => v && v.eventProductId === booth.eventProductId) || null;
  const product = products && booth.productId ? products[booth.productId] : null;
  const code =
    (entry && present(entry.variant) && entry.variant) ||
    (product && present(product.boothVariant) && product.boothVariant) ||
    (present(booth.productVariant) && booth.productVariant) ||
    null;
  if (!code) return null;
  const size = present(booth.variantSize) ? booth.variantSize : booth.area;
  const counts =
    entry && Number.isFinite(Number(entry.total)) && Number(entry.total) > 0
      ? { free: Number(entry.free) || 0, total: Number(entry.total) }
      : null;
  return { variant: code, size: present(size) ? size : null, free: counts ? counts.free : null, total: counts ? counts.total : null };
}

/** Time zone of the event's calendar-day fields (AWSDate), as in the boothBooking Lambda. */
const EVENT_TIME_ZONE = 'Europe/Berlin';

const DAY_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

/** Calendar day (YYYY-MM-DD) of an AWSDate value (a zone/time suffix is ignored), or null when blank/unparsable. */
function dayOf(value) {
  if (typeof value !== 'string') return null;
  const match = DAY_PATTERN.exec(value.trim());
  return match ? match[1] : null;
}

/** Calendar day (YYYY-MM-DD) of the instant `ms` in Europe/Berlin, or null for an invalid instant. */
function berlinDayOf(ms) {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  const parts = {};
  new Intl.DateTimeFormat('en-US', { timeZone: EVENT_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(date)
    .forEach(({ type, value }) => {
      parts[type] = value;
    });
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Whether booth booking is open for the map event at `nowMs` (server time).
 *
 * Same rule as the boothBooking Lambda (isEventBookable → EVENT_NOT_BOOKABLE): the event must be
 * active with `boothBooking` switched on, and today's calendar day in Europe/Berlin must lie inside
 * the optional window [boothBookingStart, boothBookingEnd] (AWSDate, both days inclusive;
 * null/blank/unparsable = no limit; older map responses carry neither field).
 *
 * Returns { open, reason, date }: reason 'closed' (switch off / event not active),
 * 'not-started' (date = start day) or 'ended' (date = last bookable day), as YYYY-MM-DD; null when open.
 */
export function boothBookingWindow(event, nowMs) {
  if (!event || event.boothBooking !== true || event.status !== 'active') {
    return { open: false, reason: 'closed', date: null };
  }
  const start = dayOf(event.boothBookingStart);
  const end = dayOf(event.boothBookingEnd);
  if (!start && !end) return { open: true, reason: null, date: null };
  const today = berlinDayOf(Number.isFinite(nowMs) ? nowMs : Date.now());
  if (!today) return { open: false, reason: 'closed', date: null };
  if (start && today < start) return { open: false, reason: 'not-started', date: start };
  if (end && today > end) return { open: false, reason: 'ended', date: end };
  return { open: true, reason: null, date: null };
}
